import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FloatButton } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAuth } from "../assets/utils/useAuth";
import { usePermissions } from "../assets/utils/usePermissions";
import { LEGACY_MODULE_ROLE_IDS } from "../assets/utils/accessCatalog";
import GlobalSearchOverlay from "../components/GlobalSearch/GlobalSearchOverlay";

/**
 * Match the Sidebar "Search" nav item: module `search` from the access catalog.
 * canStayOnModule applies the legacy staff-role fallback only while the RBAC
 * permission map is empty, so a seeded account is judged on its permissions alone.
 */
function useCanOpenGlobalSearch() {
  const { user } = useAuth();
  const { canStayOnModule } = usePermissions();

  return useMemo(() => {
    if (!user) return false;
    return canStayOnModule("search", LEGACY_MODULE_ROLE_IDS.search);
  }, [user, canStayOnModule]);
}

const GlobalSearchContext = createContext(null);

export function useGlobalSearch() {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    return {
      open: () => {},
      close: () => {},
      toggle: () => {},
      canSearch: false,
    };
  }
  return ctx;
}

export function GlobalSearchProvider({ children }) {
  const canSearch = useCanOpenGlobalSearch();
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  const openSearch = useCallback(() => {
    if (canSearch) setOpen(true);
  }, [canSearch]);
  const toggle = useCallback(() => {
    if (canSearch) setOpen((o) => !o);
  }, [canSearch]);

  useEffect(() => {
    if (!canSearch) return undefined;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== "k" && e.key !== "K") return;
      if (e.target?.closest?.("[data-global-search-ignore]")) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canSearch, toggle]);

  const value = useMemo(
    () => ({
      open: openSearch,
      close,
      toggle,
      canSearch,
    }),
    [openSearch, close, toggle, canSearch],
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      {canSearch ? (
        <>
          <GlobalSearchOverlay open={open} onClose={close} />
          <FloatButton
            type="primary"
            shape="circle"
            icon={<SearchOutlined />}
            tooltip="Search anywhere (Ctrl+K or ⌘K)"
            onClick={openSearch}
            style={{ insetInlineEnd: 24, bottom: 96 }}
          />
        </>
      ) : null}
    </GlobalSearchContext.Provider>
  );
}
